import React, { useMemo, useState } from "react";
import PageLoader from "components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";
import Modal from "components/ui/Modal";
import Table from "components/ui/Table";
import {
  useCreateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetOrganizationByIdQuery,
  useGetOrganizationsQuery,
  useUpdateOrganizationMutation,
} from "apis/organization";
import OrganizationEdit from "components/organization/OrganizationEdit";
import OrganizationView from "components/organization/OrganizationView";
import { formatDate } from "utils/helperFunctions";
import {
  SHARING_PILL,
  getClosedRecordTypes,
  getSharedRecordTypes,
} from "utils/sharing";

const STATUS_COLORS = {
  active: "bg-green-100 text-green-800",
  suspended: "bg-red-100 text-red-800",
  inactive: "bg-lightgray text-darkergray",
};

const Organizations = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useGetOrganizationsQuery();

  // The edit form needs the RAW organization. The list is resolved, so it shows
  // what a group INHERITS — saving that back would store the network's values
  // as group overrides, and resolved sharing arrives as booleans the form reads
  // as "inherit".
  const { data: editOrganization, isPending: isEditPending } =
    useGetOrganizationByIdQuery(isEditOpen ? selectedId : null);

  const { mutate: createOrganization, isLoading: isCreateLoading } =
    useCreateOrganizationMutation();
  const { mutate: updateOrganization } = useUpdateOrganizationMutation();
  const { mutate: deleteOrganization, isLoading: isDeleteLoading } =
    useDeleteOrganizationMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        // The group's own name, not the network's — two groups under one
        // network are otherwise indistinguishable here.
        header: "Organization",
        isFilter: true,
        minWidth: "180px",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => {
          const status = row.status || "active";
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                STATUS_COLORS[status] || STATUS_COLORS.active
              }`}
            >
              {status}
            </span>
          );
        },
        isFilter: false,
      },
      {
        accessorKey: "trustAccount.isEnabled",
        header: "Trust Account",
        cell: (row) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              row.trustAccount?.isEnabled
                ? "bg-green-100 text-green-800"
                : "bg-lightgray text-darkergray"
            }`}
          >
            {row.trustAccount?.isEnabled ? "Enabled" : "Disabled"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "sharing",
        header: "Shared Across Offices",
        // Only decisions are listed. A closed entry is a deliberate opt-out
        // from what the network or division allowed, so it earns its own pill
        // rather than reading as "not shared".
        cell: (row) => {
          const decided = [
            ...getSharedRecordTypes(row.sharing).map((recordType) => ({
              recordType,
              className: SHARING_PILL.open,
            })),
            ...getClosedRecordTypes(row.sharing).map((recordType) => ({
              recordType,
              className: SHARING_PILL.closed,
            })),
          ];

          if (!decided.length) {
            return <span className="text-sm text-darkgray">Inherit</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {decided.map(({ recordType, className }) => (
                <span
                  key={recordType.key}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}
                >
                  {recordType.name}
                </span>
              ))}
            </div>
          );
        },
        isFilter: false,
        minWidth: "180px",
      },
      {
        accessorKey: "updatedAt",
        header: "Updated",
        cell: (row) => (
          <span className="text-sm">{formatDate(row.updatedAt) || "-"}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Organization"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Organization"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "100px",
      },
    ],
    [],
  );

  if (isDataPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading organizations: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedOrganization = data?.find(
    (organization) => organization._id === selectedId,
  );

  const handleEditSubmit = (formData) => {
    updateOrganization({ id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = (formData) => {
    createOrganization(formData);
    setIsAddOpen(false);
  };

  const handleDelete = () => {
    deleteOrganization(selectedId);
    setIsDeleteOpen(false);
  };

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddOrganizationButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add Organization
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Organization"
        className="max-w-5xl"
      >
        {isEditPending || !editOrganization ? (
          <p className="py-6 text-center text-[13px] text-darkgray">
            <i className="fa-solid fa-spinner animate-spin mr-2"></i>
            Loading organization …
          </p>
        ) : (
          // Mounted only once the raw record is in hand — the form reads
          // defaultValues on mount, so a later arrival would be ignored.
          <OrganizationEdit
            key={selectedId || "organization-edit"}
            initialData={editOrganization}
            onSubmit={handleEditSubmit}
          />
        )}
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Organization"
        className="max-w-5xl"
      >
        <OrganizationEdit
          key={isAddOpen ? "organization-add-open" : "organization-add-closed"}
          initialData={{}}
          onSubmit={handleCreateSubmit}
        />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Organization"
      >
        <div>
          <p className="mb-4">
            Are you sure you want to delete "{selectedOrganization?.name}"? This
            action cannot be undone.
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              No
            </button>
            <button
              onClick={handleDelete}
              className="black-button"
              disabled={isDeleteLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Organization Details"
        className="max-w-5xl"
      >
        <OrganizationView organization={selectedOrganization} />
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Organizations"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddOrganizationButton}
          emptyText="No organizations found"
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default Organizations;
